// src/BookingManagement/MotorbikeRentalApp.js - Unified Reservations-Based Version
import React, { useState, useEffect } from "react";
import { Search, CheckCircle, XCircle, Loader, Database, AlertCircle, Calendar, Building, Shield, Lock, RefreshCw, Bike, Users } from "lucide-react";
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  orderBy,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase';

// Import enhanced AuthContext
import { useAuth } from '../contexts/AuthContext';

// Import utility functions
import { 
  calculateRentalDuration,
  checkOverdueStatus
} from './bikeUtils';

// Import sub-components (now unified)
import ActiveRentals from './ActiveRentals';
import TodaysPickups from './TodaysPickups';
import BikeReturns from './BikeReturns';
import AvailabilityChecker from './AvailabilityChecker';
import BikeCalendar from './BikeCalendar';

const UnifiedMotorbikeRentalApp = () => {
  // Get multi-tenant context
  const { 
    currentUser, 
    currentCompany, 
    getCompanyContext, 
    hasPermission, 
    authInitialized 
  } = useAuth();

  // Basic state setup
  const [activeTab, setActiveTab] = useState("booking");
  const [activeRentals, setActiveRentals] = useState([]);
  const [allReservations, setAllReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);

  // Debug state
  const [debugMode, setDebugMode] = useState(process.env.NODE_ENV === 'development');
  const [debugInfo, setDebugInfo] = useState({});

  // Get current company context
  const companyId = getCompanyContext();

  // Firebase collections - using reservations as single source of truth
  const reservationsCollection = collection(db, `companies/${companyId}/reservations`);

  // Debug function
  const updateDebugInfo = (key, value) => {
    if (debugMode) {
      setDebugInfo(prev => ({
        ...prev,
        [key]: value,
        lastUpdate: new Date().toLocaleTimeString()
      }));
      console.log(`🐛 UNIFIED DEBUG [${key}]:`, value);
    }
  };

  // Convert reservation to rental format for compatibility
  const convertReservationToRental = (reservation) => {
    const rentalData = {
      // Keep original reservation data
      ...reservation,
      
      // Map reservation fields to rental fields for compatibility
      bike: reservation.motorbikeNumber,
      bikeModel: reservation.motorbikeNumber, // Will be enhanced by bike details lookup
      startDate: reservation.pickupDateTime?.split('T')[0],
      endDate: reservation.dropoffDateTime?.split('T')[0],
      
      // Customer details mapping
      customerDetails: {
        name: reservation.customerName,
        whatsapp: reservation.whatsappNumber,
        localNumber: reservation.localNumber,
        email: reservation.email,
        licenseNumber: reservation.licenseNumber
      },
      
      // Financial details
      totalCost: parseFloat(reservation.rentalAmount) || 0,
      baseCost: parseFloat(reservation.rentalAmount) || 0,
      invoiceNumber: reservation.invoiceNumber || `RES-${reservation.id}`,
      
      // Status mapping and calculation
      originalReservationStatus: reservation.availabilityStatus,
      rentalStatus: reservation.rentalStatus || 'reserved',
      calculatedStatus: calculateRentalStatusFromReservation(reservation),
      
      // Metadata
      isFromReservation: true,
      reservationId: reservation.id,
      createdFromReservation: true,
      
      // Locations
      pickupLocation: reservation.pickupLocation || 'office',
      dropoffLocation: reservation.dropoffLocation || 'office',
      customPickupLocation: reservation.customPickupLocation,
      customDropoffLocation: reservation.customDropoffLocation,
      
      // Dates for compatibility
      createdAt: reservation.createdAt,
      updatedAt: reservation.updatedAt || reservation.createdAt
    };
    
    return rentalData;
  };

  // Calculate rental status from reservation data
  const calculateRentalStatusFromReservation = (reservation) => {
    // Handle cancelled reservations
    if (reservation.availabilityStatus === 'cancel') {
      return 'Cancelled';
    }
    
    // Handle completed/returned reservations
    if (reservation.availabilityStatus === 'returned' || reservation.rentalStatus === 'completed') {
      return 'Returned';
    }

    // Only process booked reservations as active rentals
    if (reservation.availabilityStatus !== 'booked') {
      return 'Not Active';
    }

    if (!reservation.pickupDateTime || !reservation.dropoffDateTime) {
      return 'Reserved';
    }

    // Check for manual rental status overrides first - if explicitly set to active, use it
    if (reservation.rentalStatus === 'active') {
      return 'Active';
    }

    // Get today's date and rental dates for comparison
    const today = new Date();
    const todayStr = today.getFullYear() + '-' + 
                    String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                    String(today.getDate()).padStart(2, '0');
    
    const pickupDate = new Date(reservation.pickupDateTime);
    const pickupStr = pickupDate.getFullYear() + '-' + 
                     String(pickupDate.getMonth() + 1).padStart(2, '0') + '-' + 
                     String(pickupDate.getDate()).padStart(2, '0');
    
    const dropoffDate = new Date(reservation.dropoffDateTime);
    const dropoffStr = dropoffDate.getFullYear() + '-' + 
                      String(dropoffDate.getMonth() + 1).padStart(2, '0') + '-' + 
                      String(dropoffDate.getDate()).padStart(2, '0');

    // Debug log
    console.log(`Status calc for ${reservation.customerName}:`, {
      today: todayStr,
      pickup: pickupStr,
      dropoff: dropoffStr,
      rentalStatus: reservation.rentalStatus
    });

    // Determine status based on dates
    if (todayStr < pickupStr) {
      // Future rental
      return 'Reserved';
    } else if (todayStr >= pickupStr && todayStr < dropoffStr) {
      // Within rental period - should be active
      return 'Active';
    } else if (todayStr === dropoffStr) {
      // Due back today
      return 'Due Today';
    } else {
      // Past due date
      return 'Overdue';
    }
  };

  // Get rentals that should appear as "active" (booked reservations only)
  const getActiveRentalsFromReservations = (reservations) => {
    return reservations
      .filter(reservation => {
        // Only include booked reservations that aren't returned/cancelled
        return reservation.availabilityStatus === 'booked' && 
               reservation.availabilityStatus !== 'returned' &&
               reservation.availabilityStatus !== 'cancel';
      })
      .map(convertReservationToRental)
      .filter(rental => rental.calculatedStatus !== 'Not Active'); // Additional safety filter
  };

  // Initialize Firebase connection with unified reservations approach
  useEffect(() => {
    if (!authInitialized || !companyId || !currentCompany) {
      setIsLoading(false);
      return;
    }

    initializeUnifiedConnection();
  }, [authInitialized, companyId, currentCompany]);

  const initializeUnifiedConnection = async () => {
    try {
      setConnectionStatus('connecting');
      setError(null);
      console.log(`🎯 UNIFIED: Initializing reservations-based system for company: ${currentCompany.name} (${companyId})`);

      // Set tenant info
      const tenantData = {
        companyId: companyId,
        companyName: currentCompany.name,
        userId: currentUser.id
      };
      setTenantInfo(tenantData);
      updateDebugInfo('tenantInfo', tenantData);

      // Load initial reservations data
      const q = query(reservationsCollection, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const initialReservations = [];
      
      querySnapshot.forEach((doc) => {
        initialReservations.push({ id: doc.id, ...doc.data() });
      });
      
      setAllReservations(initialReservations);
      
      // Convert to active rentals format
      const activeRentalData = getActiveRentalsFromReservations(initialReservations);
      setActiveRentals(activeRentalData);
      
      console.log(`✅ UNIFIED: Loaded ${initialReservations.length} reservations, ${activeRentalData.length} active rentals for ${currentCompany.name}`);
      updateDebugInfo('initialData', {
        totalReservations: initialReservations.length,
        activeRentals: activeRentalData.length,
        breakdown: {
          booked: initialReservations.filter(r => r.availabilityStatus === 'booked').length,
          pending: initialReservations.filter(r => r.availabilityStatus === 'pending').length,
          cancelled: initialReservations.filter(r => r.availabilityStatus === 'cancel').length,
          returned: initialReservations.filter(r => r.availabilityStatus === 'returned').length
        }
      });

      // Set up real-time subscription to reservations
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const updatedReservations = [];
        querySnapshot.forEach((doc) => {
          updatedReservations.push({ id: doc.id, ...doc.data() });
        });
        
        console.log(`🔄 UNIFIED: Real-time reservation updates for ${currentCompany.name}: ${updatedReservations.length} reservations`);
        
        setAllReservations(updatedReservations);
        
        // Convert to active rentals
        const updatedActiveRentals = getActiveRentalsFromReservations(updatedReservations);
        setActiveRentals(updatedActiveRentals);
        
        setLastUpdated(new Date());
        setConnectionStatus('connected');
        setIsLoading(false);
        
        updateDebugInfo('realtimeUpdate', {
          timestamp: new Date().toISOString(),
          totalReservations: updatedReservations.length,
          activeRentals: updatedActiveRentals.length,
          statusBreakdown: {
            reserved: updatedActiveRentals.filter(r => r.calculatedStatus === 'Reserved').length,
            active: updatedActiveRentals.filter(r => r.calculatedStatus === 'Active').length,
            overdue: updatedActiveRentals.filter(r => r.calculatedStatus === 'Overdue').length,
            dueToday: updatedActiveRentals.filter(r => r.calculatedStatus === 'Due Today').length
          }
        });
      }, (error) => {
        console.error('❌ UNIFIED: Error in reservations subscription:', error);
        setConnectionStatus('error');
        setError(`Reservations sync error: ${error.message}`);
      });

      setConnectionStatus('connected');
      setIsLoading(false);

      // Cleanup function
      return () => {
        console.log(`🧹 UNIFIED: Cleaning up reservations subscription for ${currentCompany.name}`);
        if (unsubscribe) unsubscribe();
      };

    } catch (error) {
      console.error(`❌ UNIFIED: Error initializing for ${currentCompany?.name}:`, error);
      setError(`Failed to connect to unified system for ${currentCompany?.name}: ${error.message}`);
      setConnectionStatus('error');
      setIsLoading(false);
      updateDebugInfo('initError', error.message);
    }
  };

  // Handle updates from child components
  const handleRentalsUpdate = (updatedRentals) => {
    setActiveRentals(updatedRentals);
    setLastUpdated(new Date());
    
    console.log(`📊 UNIFIED: Rental data updated for ${currentCompany?.name}: ${updatedRentals.length} active rentals`);
    updateDebugInfo('childUpdate', {
      count: updatedRentals.length,
      timestamp: new Date().toISOString()
    });
  };

  // Refresh data manually
  const refreshUnifiedData = async () => {
    if (!companyId) return;
    
    setIsLoading(true);
    try {
      console.log('🔄 UNIFIED: Manual refresh triggered');
      await initializeUnifiedConnection();
      updateDebugInfo('manualRefresh', new Date().toISOString());
    } catch (error) {
      setError(`Failed to refresh unified data for ${currentCompany?.name}: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Utility functions for statistics
  const getTodaysPickups = (rentals) => {
    const today = new Date().toISOString().split('T')[0];
    return rentals.filter(rental => {
      if (!rental.startDate) return false;
      return rental.startDate === today && rental.calculatedStatus === 'Reserved';
    });
  };

  const getRentalsDueToday = (rentals) => {
    const today = new Date().toISOString().split('T')[0];
    return rentals.filter(rental => {
      if (!rental.endDate) return false;
      return rental.endDate === today && rental.calculatedStatus !== 'Returned';
    });
  };

  const getOverdueRentals = (rentals) => {
    return rentals.filter(rental => rental.calculatedStatus === 'Overdue');
  };

  // Debug system state
  const debugSystemState = () => {
    console.log('🐛 UNIFIED SYSTEM DEBUG REPORT:');
    console.log('='.repeat(60));
    console.log('🏢 Company:', {
      id: companyId,
      name: currentCompany?.name,
      user: currentUser?.username
    });
    console.log('📊 Data Summary:', {
      totalReservations: allReservations.length,
      reservationBreakdown: {
        booked: allReservations.filter(r => r.availabilityStatus === 'booked').length,
        pending: allReservations.filter(r => r.availabilityStatus === 'pending').length,
        cancelled: allReservations.filter(r => r.availabilityStatus === 'cancel').length,
        returned: allReservations.filter(r => r.availabilityStatus === 'returned').length
      },
      activeRentals: activeRentals.length,
      activeRentalStatuses: {
        reserved: activeRentals.filter(r => r.calculatedStatus === 'Reserved').length,
        active: activeRentals.filter(r => r.calculatedStatus === 'Active').length,
        overdue: activeRentals.filter(r => r.calculatedStatus === 'Overdue').length,
        dueToday: activeRentals.filter(r => r.calculatedStatus === 'Due Today').length
      }
    });
    console.log('🔄 Connection:', {
      status: connectionStatus,
      lastUpdated: lastUpdated?.toISOString(),
      error: error
    });
    console.log('🐛 Debug Info:', debugInfo);
    console.log('='.repeat(60));
  };

  // Calculate stats using unified reservations data
  const stats = {
    totalActiveBookings: activeRentals.filter(rental => rental && rental.calculatedStatus === "Active").length,
    totalUpcomingBookings: activeRentals.filter(rental => rental && rental.calculatedStatus === "Reserved").length,
    totalReturnsDueToday: activeRentals.filter(rental => rental && rental.calculatedStatus === "Due Today").length,
    totalOverdueReturns: activeRentals.filter(rental => rental && rental.calculatedStatus === "Overdue").length
  };

  // Connection status component
  const UnifiedConnectionStatus = () => {
    const statusConfig = {
      connecting: { 
        color: 'text-yellow-400', 
        icon: Loader, 
        text: 'Connecting to Unified System...', 
        bgColor: 'bg-yellow-900/20',
        borderColor: 'border-yellow-700',
        spin: true 
      },
      connected: { 
        color: 'text-green-400', 
        icon: Users, 
        text: 'Unified Reservations System Connected', 
        bgColor: 'bg-green-900/20',
        borderColor: 'border-green-700',
        spin: false 
      },
      error: { 
        color: 'text-red-400', 
        icon: AlertCircle, 
        text: 'Unified System Connection Error', 
        bgColor: 'bg-red-900/20',
        borderColor: 'border-red-700',
        spin: false 
      }
    };

    const { color, icon: Icon, text, bgColor, borderColor, spin } = statusConfig[connectionStatus];

    return (
      <div className={`flex items-center gap-2 ${color} ${bgColor} ${borderColor} border rounded-lg px-3 py-2`}>
        <Icon size={16} className={spin ? 'animate-spin' : ''} />
        <div className="flex flex-col">
          <span className="text-xs font-medium">{text}</span>
          <div className="flex items-center gap-2 text-xs opacity-75">
            <Building size={12} />
            <span>{currentCompany?.name}</span>
            <span>•</span>
            <span>{allReservations.length} reservations</span>
            <span>•</span>
            <span>{activeRentals.length} active</span>
            {lastUpdated && connectionStatus === 'connected' && (
              <>
                <span>•</span>
                <span>Updated: {lastUpdated.toLocaleTimeString()}</span>
              </>
            )}
          </div>
        </div>
        {connectionStatus === 'connected' && (
          <div className="flex gap-1">
            <button
              onClick={refreshUnifiedData}
              disabled={isLoading}
              className="p-1 hover:bg-green-800/30 rounded transition-colors"
              title={`Refresh unified data for ${currentCompany?.name}`}
            >
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            {debugMode && (
              <button
                onClick={debugSystemState}
                className="p-1 hover:bg-blue-800/30 rounded transition-colors"
                title="Debug unified system state"
              >
                <AlertCircle size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Permission check
  if (!hasPermission('bookings')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white flex items-center justify-center">
        <div className="text-center">
          <Shield className="mx-auto h-20 w-20 text-gray-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Access Denied</h3>
          <p className="text-gray-500 text-lg">You don't have permission to access the unified booking management system.</p>
          <p className="text-gray-600 text-sm mt-2">Contact your company administrator for access.</p>
        </div>
      </div>
    );
  }

  // No company context available
  if (!authInitialized || !companyId || !currentCompany) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-20 w-20 text-yellow-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">No Company Context</h3>
          <p className="text-gray-500 text-lg">Unified reservations system requires valid company context.</p>
          <p className="text-gray-600 text-sm mt-2">Please ensure you're logged in with a valid company account.</p>
        </div>
      </div>
    );
  }

  // Loading screen
  if (isLoading && connectionStatus === 'connecting') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white flex items-center justify-center">
        <div className="text-center">
          <Loader className="animate-spin text-orange-500 mx-auto mb-4" size={48} />
          <h2 className="text-2xl font-bold text-white mb-2">Connecting to Unified System</h2>
          <p className="text-gray-400 mb-4">Loading reservations-based rental management for {currentCompany?.name}...</p>
          <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-center justify-center gap-2 text-blue-300 text-sm mb-2">
              <Lock size={16} />
              <span>Single source of truth: Reservations collection</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-green-300 text-xs">
              <Users size={14} />
              <span>Unified data architecture for {currentCompany?.name}</span>
            </div>
          </div>
          <div className="mt-4">
            <UnifiedConnectionStatus />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white">
      <div className="container mx-auto py-6 px-4 sm:px-6">
        {/* Header */}
        <header className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-6 md:mb-0">
              <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-purple-600 flex items-center gap-3">
                Unified Rental System
              </h1>
            </div>
            
            <div className="flex flex-col gap-3">
              <UnifiedConnectionStatus />
              
              <div className="grid grid-cols-4 gap-4 bg-gray-800 p-3 rounded-xl shadow-xl border border-gray-700">
                <div className="p-3 bg-gray-700 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-400">{stats.totalActiveBookings}</p>
                  <p className="text-xs text-gray-300 mt-1">Active</p>
                </div>
                <div className="p-3 bg-gray-700 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-400">{stats.totalUpcomingBookings}</p>
                  <p className="text-xs text-gray-300 mt-1">Upcoming</p>
                </div>
                <div className="p-3 bg-gray-700 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-400">{stats.totalReturnsDueToday}</p>
                  <p className="text-xs text-gray-300 mt-1">Due Today</p>
                </div>
                <div className="p-3 bg-gray-700 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-400">{stats.totalOverdueReturns}</p>
                  <p className="text-xs text-gray-300 mt-1">Overdue</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/50 border border-red-700 rounded-lg flex items-center">
            <AlertCircle className="text-red-400 mr-3 flex-shrink-0" size={20} />
            <div className="flex-grow">
              <p className="text-red-300 font-medium">Unified System Connection Error</p>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
            <button
              onClick={() => {
                setError(null);
                initializeUnifiedConnection();
              }}
              className="ml-3 px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
            >
              Retry
            </button>
          </div>
        )}
        
        {/* Navigation Tabs */}
        <div className="bg-gray-800 rounded-xl shadow-xl overflow-hidden mb-8 border border-gray-700">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab("booking")}
              className={`px-6 py-4 flex items-center gap-2 font-medium border-b-2 whitespace-nowrap ${
                activeTab === "booking" 
                  ? "border-orange-500 text-orange-400" 
                  : "border-transparent text-gray-400 hover:text-gray-200"
              }`}
            >
              <CheckCircle className="w-5 h-5" />
              Active Rentals
              {stats.totalActiveBookings > 0 && (
                <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                  {stats.totalActiveBookings}
                </span>
              )}
              <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded-full">
                Unified
              </span>
            </button>
            
            <button
              onClick={() => setActiveTab("availability")}
              className={`px-6 py-4 flex items-center gap-2 font-medium border-b-2 whitespace-nowrap ${
                activeTab === "availability" 
                  ? "border-orange-500 text-orange-400" 
                  : "border-transparent text-gray-400 hover:text-gray-200"
              }`}
            >
              <Search className="w-5 h-5" />
              Check Availability
              <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded-full">
                Unified
              </span>
            </button>
            
            <button
              onClick={() => setActiveTab("calendar")}
              className={`px-6 py-4 flex items-center gap-2 font-medium border-b-2 whitespace-nowrap ${
                activeTab === "calendar" 
                  ? "border-orange-500 text-orange-400" 
                  : "border-transparent text-gray-400 hover:text-gray-200"
              }`}
            >
              <Calendar className="w-5 h-5" />
              Booking Calendar
              <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded-full">
                Unified
              </span>
            </button>
            
            <button
              onClick={() => setActiveTab("pickups")}
              className={`px-6 py-4 flex items-center gap-2 font-medium border-b-2 whitespace-nowrap ${
                activeTab === "pickups" 
                  ? "border-orange-500 text-orange-400" 
                  : "border-transparent text-gray-400 hover:text-gray-200"
              }`}
            >
              <CheckCircle className="w-5 h-5" />
              Today's Pickups
              {getTodaysPickups(activeRentals).length > 0 && (
                <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                  {getTodaysPickups(activeRentals).length}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setActiveTab("returns")}
              className={`px-6 py-4 flex items-center gap-2 font-medium border-b-2 whitespace-nowrap ${
                activeTab === "returns" 
                  ? "border-orange-500 text-orange-400" 
                  : "border-transparent text-gray-400 hover:text-gray-200"
              }`}
            >
              <XCircle className="w-5 h-5" />
              Returns
              {(stats.totalReturnsDueToday + stats.totalOverdueReturns) > 0 && (
                <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full">
                  {stats.totalReturnsDueToday + stats.totalOverdueReturns}
                </span>
              )}
            </button>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {activeTab === "booking" && (
            <ActiveRentals 
              activeRentals={activeRentals}
              setActiveRentals={setActiveRentals}
              onRentalsUpdate={handleRentalsUpdate}
              currentUser={currentUser}
              currentCompany={currentCompany}
              getCompanyContext={getCompanyContext}
            />
          )}

          {activeTab === "availability" && (
            <AvailabilityChecker 
              currentUser={currentUser}
              currentCompany={currentCompany}
              getCompanyContext={getCompanyContext}
            />
          )}

          {activeTab === "calendar" && (
            <BikeCalendar 
              activeRentals={activeRentals}
              setActiveRentals={setActiveRentals}
              onRentalsUpdate={handleRentalsUpdate}
              currentUser={currentUser}
              currentCompany={currentCompany}
              getCompanyContext={getCompanyContext}
              debugMode={debugMode}
            />
          )}

          {activeTab === "pickups" && (
            <TodaysPickups 
              activeRentals={activeRentals}
              setActiveRentals={setActiveRentals}
              onRentalsUpdate={handleRentalsUpdate}
              currentUser={currentUser}
              currentCompany={currentCompany}
              getCompanyContext={getCompanyContext}
            />
          )}

          {activeTab === "returns" && (
            <BikeReturns 
              activeRentals={activeRentals}
              setActiveRentals={setActiveRentals}
              onRentalsUpdate={handleRentalsUpdate}
              currentUser={currentUser}
              currentCompany={currentCompany}
              getCompanyContext={getCompanyContext}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UnifiedMotorbikeRentalApp;